
import {
  computed, defineComponent, ref, watch,
} from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useLocalI18n } from '@/hooks/useLocalI18n';
import ActiveTable from '@/components/activeTable/ActiveTable.vue';
import {
  ActionType,
  ActiveTableAction,
  ActiveTableColumn,
  ActiveTableColumnFormat,
  useActiveTable,
} from '@/components/activeTable/useActiveTable';
import { ConstructorTemplate, useConstructor } from '@/hooks/useConstructor';
import { ActiveFormField, ActiveFormFieldType } from '@/hooks/useActiveForm';
import { useDefaultCompany } from '@/hooks/useDefaultCompany';
import { useDialog } from '@/hooks/useDialog';
import { useErrors } from '@/hooks/useErrors';
import SelectInput from '@/components/selectInput/SelectInput.vue';
import { useTemplateFilters } from '@/hooks/useTemplateFilters';
import { ScreenBreakpoints, useBreakpoint } from '@/hooks/useBreakpoint';
import { OrderDirection } from '@/store/modules/api';

export default defineComponent({
  name: 'Index',
  components: { ActiveTable, SelectInput },
  setup() {
    const { t } = useLocalI18n('panel.constructor.templates');
    const { t: rootT } = useI18n();
    const {
      company,
    } = useDefaultCompany();

    const {
      fetchConstructorTemplates,
      removeConstructorTemplate,
    } = useConstructor();

    const router = useRouter();

    const {
      confirmDialog,
    } = useDialog();

    const {
      clearErrors,
      setErrors,
    } = useErrors();

    const { filterModel, filterOptions } = useTemplateFilters();

    const breakpoint = useBreakpoint();

    const {
      records,
      columns,
      actions,
      sort,
      fetchData: refetchConstructorTemplates,
    } = useActiveTable<ConstructorTemplate, ConstructorTemplate, 'id'>({
      keyField: 'id',
      async fetch({ params, signal }) {
        const { response } = await fetchConstructorTemplates(
          {
            ...params,
            signal,
            filters: {
              ...params.filters,
              ...(filterModel.value.filter
                && filterModel.value.filter !== 'reports' ? {
                  production_type: filterModel.value.filter,
                } : {}),
            },
          },
        );

        return {
          ...response,
          results: (filterModel.value.filter === 'reports'
            ? response.results.filter((t) => t.template_type.name.includes('_report'))
            : response.results),
        };
      },
      defaultSort: computed(() => ([
        {
          key: 'updated_at',
          direction: OrderDirection.desc,
        },
      ])),
      defaultLimit: ref(1000),
      filters: computed<Array<ActiveFormField<ConstructorTemplate>>>(() => ([
        {
          key: 'company_id',
          field: 'company_id',
          type: ActiveFormFieldType.input,
          defaultValue: company.value?.id,
        },
      ])),
      columns: computed<Array<ActiveTableColumn<ConstructorTemplate>>>(() => ([
        {
          key: 'id',
          field: 'id',
          label: t('column.id'),
          width: breakpoint.value === ScreenBreakpoints.tablet
            ? 'minmax(80px, 100px)'
            : 'minmax(80px, 100px)',
        },
        {
          key: 'description',
          field: 'template_type.description',
          label: t('column.description'),
          isSortable: true,
          width: breakpoint.value === ScreenBreakpoints.tablet
            ? '2fr'
            : 'minmax(150px, 1fr)',
        },
        {
          key: 'updated_at',
          field: 'updated_at',
          format: ActiveTableColumnFormat.dateTime,
          label: t('column.updated_at'),
          isSortable: true,
          width: breakpoint.value === ScreenBreakpoints.tablet
            ? 'minmax(140px, 140px)'
            : 'minmax(140px, 140px)',
        },
        {
          key: 'short_name',
          field: 'short_name',
          label: t('column.name'),
          width: breakpoint.value === ScreenBreakpoints.tablet
            ? '2fr'
            : 'minmax(150px, 1fr)',
        },
        {
          key: 'author',
          field: 'author',
          label: t('column.author'),
          width: breakpoint.value === ScreenBreakpoints.tablet
            ? '2fr'
            : 'minmax(150px, 1fr)',
        },
      ])),
      actions: computed<Array<ActiveTableAction<ConstructorTemplate, 'id'>>>(() => ([
        {
          key: 'edit',
          icon: 'pencil',
          async handler({ selectedItems: [id] }) {
            await router.push({
              name: 'panel-constructor-template',
              params: {
                id,
              },
            });
          },
          types: [ActionType.record],
        },
        {
          key: 'remove',
          icon: 'close',
          async handler({ selectedItems: [id] }) {
            clearErrors();
            const { result, closeDialog } = await confirmDialog({
              message: t('action.remove.message'),
            });
            if (result) {
              const { status, response } = await removeConstructorTemplate(id);
              if (!status) {
                setErrors(Object.entries(response) as any);
              }
              await refetchConstructorTemplates();
            }
            closeDialog();
          },
          types: [ActionType.record],
        },
      ])),
    });

    watch(() => filterModel.value.filter, () => {
      refetchConstructorTemplates();
    });

    return {
      t,
      records,
      columns,
      actions,
      sort,
      rootT,
      filterModel,
      filterOptions,
    };
  },
});
